import Breadcrumbs from '@components/molecules/products/breadcrumbs/breadcrumbs';
import clsx from 'clsx';
import DefaultLayout from '@layouts/default-layout';
import dynamic from 'next/dynamic';
import extractFromContext from '@helpers/utils/extract-from-context';
import Head, { generateAlternateLink } from '@components/atoms/head/head';
import logger from '@helpers/utils/logger/client';
import { getCombinedTimestamp } from '@helpers/utils/analytics-utils';
import ProductDescription from '@components/molecules/products/product-description/product-description';
import ProductReport from '@components/molecules/products/product-report/product-report';
import ProductSchema from '@components/structured-schemas/product-schema';
import ProductService from '@services/product-service';
import React, { useEffect, useRef } from 'react';
import RequestError from '@errors/request-error';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import useProductQuery from '@hooks/product/use-product-query';
import useRecentlyViewedProducts from '@hooks/product/use-recently-viewed-products';
import useUser from '@hooks/user/use-user';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { ContentsLocalizedPath } from '@interfaces/models/contentsLocalizedPath';
import { dehydrate, DehydratedState, QueryClient, useHydrate } from '@tanstack/react-query';
import { extractUrlfromPrerenderDataReq } from '@helpers/routing';
import { getWelcomeVoucher } from '@api/contents';
import { getBreadcrumbs } from '@api/breadcrumbs';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import { mapProduct, mapProductPageUrl } from '@helpers/utils/analytics';
import { Product } from '@interfaces/models/product';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Shipping } from '@interfaces/models/shipping';
import { SSRConfig } from 'next-i18next';
import { UpdateDataLayerComponent } from '@context/analytics.context';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';
import { WelcomeVoucher } from '@interfaces/models/welcomeVoucher';
import { SellerStats } from '@interfaces/models/seller-info/seller-stats';
import {
  BreadcrumbsQueryKeys,
  FeatureFlagsKeys,
  GlobalQueryKeys,
  ProductPageQueryKeys,
  ProductPdpPageQueryKeys,
} from '@enums/react-query-keys';
import { ImagesService } from '@services/image-service';
import Environment from '@config/environment';
import imageQuality from '@config/image';
import useInitialRequestUrlQuery from '@hooks/use-initial-request-url-query';
import { usePreferences } from '@context/preferences.context';
import { languageToLocaleMap } from '@config/localization';
import { getAppInitialProps } from '@helpers/initial-props';
import SemPdpSimilarProductsFeed from '@components/product/sem-pdp-similar-products/sem-pdp-similar-products';
import { SEM_PDP_REWRITE_HEADER } from '@helpers/feature-flags/custom-headers';
import usePdpWelcomeVoucherQuery from '@hooks/product/use-pdp-welcome-voucher-query';
import PdpWelcomeVoucherBanner from '@components/product/pdp-welcome-voucher-banner/pdp-welcome-voucher-banner';
import { AppCookies } from '@enums/cookies';
import getServerFeatureFlag from '@hooks/bucket-test/use-ssr-feature-flag';
import useFeatureFlag from '@hooks/use-feature-flags';
import { ProductFeedSource } from '@interfaces/api/payloads/product-feed-payload';
import { getSEMPDPFeedQuery } from '@api/feeds';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import { isNewUser } from '@helpers/utils/date-utils';
import { NEW_USER_THRESHOLD_DAYS } from '@constants/onboarding-benefits-block';
import { useRouter } from 'next/router';
import { isWebview } from '@helpers/routing';
import { getVisitorIdentityFromCookie } from '@helpers/utils/visitor-identity-cookie';
import { VisitorType } from '@enums/visitor-identity';
import { useAnalytics } from '@context/analytics.context';
import styles from './index.module.scss';

const ProductCategoriesSuggestions = dynamic(
  () => import('@components/molecules/products/product-categories-suggestions/product-categories-suggestions'),
  { ssr: false },
);

const ProductDiscussionWrapper = dynamic(
  () => import('@components/molecules/products/product-discussion/product-discussion-wrapper'),
  { ssr: false },
);

const PdpRecommendationModules = dynamic(
  () => import('@components/product/pdp-recommendation-modules/pdp-recommendation-modules'),
  {
    ssr: false,
  },
);

const RecentlyViewedProductsWrapper = dynamic(
  () => import('@components/product/recently-viewed-products/recently-viewed-products-wrapper'),
  { ssr: false },
);

const BenefitsBlock = dynamic(() => import('@components/organisms/benefits-block/benefits-block'), { ssr: false });

const HeroPDP = dynamic(() => import('@components/hero-pdp/hero-pdp'));

const PdpTop = dynamic(() => import('@components/product/pdp-top/pdp-top'));
type ProductPageProps = {
  breadcrumbs: Breadcrumb[];
  dehydratedState: DehydratedState;
  userAgent?: string;
  isSemPDP?: boolean;
  isWelcomeOfferLDFlagEnabled?: boolean;
  showPdpBanner?: boolean;
};

const ProductPage: NextPageWithLayout<ProductPageProps> = (props): React.JSX.Element => {
  const { breadcrumbs, dehydratedState, userAgent, isWelcomeOfferLDFlagEnabled, showPdpBanner } = props;
  useHydrate(dehydratedState);

  const { user, isAuthenticated } = useUser();
  const { product } = useProductQuery();
  const { addRecentlyViewedProduct } = useRecentlyViewedProducts();
  const { isRecirculation } = usePdpLayout();
  const { isSemPDP } = useFeatureFlag();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const isPromotionalPdp = isHeroPdpStyles || isSemPDP || isRecirculation; // Benefits block custom styles
  /*
   * For future A/B tests, we’d exclude bots and crawlers from flag evaluation by default, evaluating the flag only for legitimate users.
   * We could still provide an option to include them if a specific feature requires it.
   * This way, we wouldn’t have to manually add filters to exclude bots/crawlers/webviews or check if the user is legit every time.
   * While this can be handled globally in the future, for now we are adding it here.
   */
  const { query } = useRouter();
  const isWebView = isWebview(query);
  const getVisitorIdentity = getVisitorIdentityFromCookie();
  const isUserLegit = getVisitorIdentity?.visitorType === VisitorType.NormalUser;

  const { isFeatureEnabled, state: ldStatus } = useBucket();
  const dsaFeature = isFeatureEnabled((f) => f.reportAnItemOrUser, true) || false;

  const isNewOrUnauthenticated = !isAuthenticated || isNewUser(user?.inscriptionDate, NEW_USER_THRESHOLD_DAYS);

  const isPdpOnboardingBenefitsBlockEnabled =
    isUserLegit &&
    !isWebView &&
    isNewOrUnauthenticated &&
    isFeatureEnabled((f) => f.enablePdpOnboardingBenefitsBlock, true);
  const {
    bannerText: voucherBannerText,
    voucherCode,
    onClose,
    displayPdpTopBanner,
  } = usePdpWelcomeVoucherQuery(product?.id, isRecirculation, showPdpBanner, isWelcomeOfferLDFlagEnabled);

  const { scriptsEventData } = useAnalytics();
  const pageviewSentRef = useRef(false);

  // Manually trigger pageview on SEM PDP pages BEFORE banner events are sent
  // This requires adding '/product/sem_pdp' to TagCommander's noPageViewActionScreens list
  const isLdReady = ldStatus === LDState.SUCCESS;
  const isSemPdpPageviewEnabled = isLdReady ? isFeatureEnabled((f) => f.enablePageviewStEventsQueue, false) : false;
  useEffect(() => {
    if (pageviewSentRef.current || !isSemPDP || !isLdReady || !isSemPdpPageviewEnabled) {
      return;
    }

    if (!scriptsEventData?.isTcEvents1Loaded || !scriptsEventData?.isTcEvents7Loaded) {
      return;
    }

    if (!window.trackSnowplowPageView) {
      return;
    }

    // Send pageview - banner components use executeAfterPageviewIfSemPDP to wait for this
    try {
      window.trackSnowplowPageView();
      window.__vc_semPdpPageviewSent = true;
      pageviewSentRef.current = true;
      logger.info(
        `${getCombinedTimestamp()} [SEM PDP PAGEVIEW] ========== PAGEVIEW SENT (FF=${isSemPdpPageviewEnabled}, LD=${ldStatus}) ==========`,
      );
    } catch (error) {
      // If pageview fails, don't set the flag so banner events won't wait unnecessarily
      logger.error(`${getCombinedTimestamp()} [SEM PDP PAGEVIEW] Failed to send pageview:`, error);
    }
  }, [
    isSemPDP,
    isSemPdpPageviewEnabled,
    isLdReady,
    ldStatus,
    scriptsEventData?.isTcEvents1Loaded,
    scriptsEventData?.isTcEvents7Loaded,
  ]);

  const dataLayer = {
    env_template: 'product',
    screen_name: '/product',
    screen_category: 'product',
    screen_subcategory: 'product',
    ...mapProduct(product),
    ...mapProductPageUrl(product),
    ...(isSemPDP ? { catalog_version: 'hero pdp', screen_name: '/product/sem_pdp' } : {}),
  };

  useEffect(() => {
    addRecentlyViewedProduct(product);
  }, [product]);

  // Reset pageview sent flag on route change
  const router = useRouter();
  useEffect(() => {
    const handleRouteChange = () => {
      // Reset pageview sent flag on route change
      window.__vc_semPdpPageviewSent = false;
    };
    router.events?.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events?.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  const productImage = ImagesService.improveSEOProductPathImg(product?.pictures?.[0].path, product?.path);
  const { initialRequestUrl } = useInitialRequestUrlQuery();
  const { locale } = usePreferences();

  return (
    <>
      <UpdateDataLayerComponent
        data={dataLayer}
        userAgent={userAgent}
      />
      <Head
        metadata={product?.metadata}
        alternateVersions={product?.alternateVersions ?? []}
      >
        <meta
          key="og:image"
          property="og:image"
          content={`${Environment.imagesBaseUrl}${ImagesService.getResizingPrefix(
            productImage,
          )}/w=500,q=${imageQuality},f=auto,${productImage}`}
        />
        <meta
          key="og:url"
          property="og:url"
          content={initialRequestUrl.href.replace('.origin-fenx', '').replace('.fenx', '')}
        />
        <meta
          key="og:locale"
          property="og:locale"
          content={locale}
        />
        {Array.from(languageToLocaleMap).map(([, value]) => (
          <meta
            key={value}
            property="og:locale:alternate"
            content={value}
          />
        ))}
        <meta
          key="og:site_name"
          property="og:site_name"
          content="Vestiaire Collective"
        />
      </Head>
      <main className={styles.productPage}>
        <div style={{ minHeight: displayPdpTopBanner ? '44px' : 'auto' }}>
          {displayPdpTopBanner && (
            <PdpWelcomeVoucherBanner
              bannerText={voucherBannerText}
              closeBanner={onClose}
              voucherCode={voucherCode}
            />
          )}
        </div>
        <ProductSchema
          product={product}
          seoMetaData={product?.metadata}
        />
        <div
          className={clsx(
            styles.productPage__top,
            isRecirculation && styles['productPage__top--downsized'],
            'innerContainer',
          )}
        >
          {breadcrumbs && (
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              isCatalogPage={false}
              lastItemIsHeading={false}
              customClass={clsx(styles.productPage__top__breadcrumb, 'd-none d-md-flex')}
            />
          )}
          {isRecirculation ? (
            <HeroPDP includeProductSchema={false} />
          ) : (
            <PdpTop isWelcomeOfferLDFlagEnabled={isWelcomeOfferLDFlagEnabled} />
          )}
        </div>
        {isPdpOnboardingBenefitsBlockEnabled && (
          <BenefitsBlock
            pageType="product_page"
            className={clsx(
              styles.productPage__benefitsBlock,
              isPromotionalPdp && styles['productPage__benefitsBlock--promotional'],
            )}
          />
        )}
        {isSemPDP && <SemPdpSimilarProductsFeed />}
        {(isRecirculation || isSemPDP) && <PdpRecommendationModules feedType="users_also_liked" />}
        <section
          data-cy="product-more-details"
          className={styles.productPage__moreDetails}
        >
          <ProductDescription canReportProducts={user?.productReporter} />
        </section>
        {!isRecirculation && !isSemPDP && <PdpRecommendationModules />}
        <RecentlyViewedProductsWrapper />
        <ProductDiscussionWrapper />
        <section className={styles.productPage__categories}>
          <ProductCategoriesSuggestions product={product} />
          {user && dsaFeature && (
            <ProductReport
              productId={product.id}
              email={user.email}
              local={user.language.code}
            />
          )}
        </section>
      </main>
    </>
  );
};

ProductPage.Layout = [DefaultLayout];
ProductPage.PageType = 'ProductPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const productSlugs = context?.params?.productSlug as string[];
  const fullSlug = `/${productSlugs?.join('/')}`;

  const { serverAnonymousId, randomRangeId } = getOrCreateLDCookiesSsr(context);

  const queryClient = new QueryClient();
  const initialProps = await getAppInitialProps(context, queryClient);
  const semPdp = context.req.headers[SEM_PDP_REWRITE_HEADER] as string;

  const { getFeatureFlag } = getServerFeatureFlag(context, serverAnonymousId, randomRangeId);

  // Extract id from slug
  let id: string | undefined;
  try {
    [id] = fullSlug.match(/\d+\.shtml$/);
    [id] = id.split('.shtml');
  } catch (err) {
    return {
      notFound: true,
    };
  }

  /*
    remove _next/data request prefix
    When do clientside navigation, if page fails, we will make sure it redirects to the correct path
  */
  const { req, query: contextQuery } = context;
  req.url = extractUrlfromPrerenderDataReq(contextQuery, 'productSlug');

  const {
    locale,
    preferences: { language },
    userId,
    serverAxiosRequestData,
  } = extractFromContext(context);

  try {
    const mandatoryRequests = [
      serverSideTranslations(locale, [
        'default-layout',
        'vc-authentication',
        'a11y',
        'common',
        'seller-stats',
        'benefits',
      ]),
      ProductService.getProduct(id, serverAxiosRequestData),
      ProductService.getProductShippingInformation(id, serverAxiosRequestData),
    ];

    const productID = parseInt(id, 10);

    // These requests only get triggered based on certain conditions and may fail
    const optionalRequests = [
      getBreadcrumbs(semPdp || fullSlug, serverAxiosRequestData),
      getFeatureFlag('enable-new-welcome-offer-pdp'),
      queryClient.fetchQuery<WelcomeVoucher>({
        queryKey: [GlobalQueryKeys.WELCOME_VOUCHER, semPdp ? 'HERO_PDP' : 'PDP', id],
        queryFn: () => getWelcomeVoucher(semPdp ? 'HERO_PDP' : 'PDP', serverAnonymousId, serverAxiosRequestData, id),
      }),
      // Get similar products for SEM PDP only
      ...(semPdp
        ? [
            queryClient.fetchQuery(
              getSEMPDPFeedQuery(
                serverAxiosRequestData.preferences,
                userId,
                {
                  productID,
                  source: ProductFeedSource.SEMPDP,
                },
                serverAnonymousId,
                serverAxiosRequestData,
              ),
            ),
          ]
        : [null]),
      // Get the Seller stats for the new seller block
      queryClient.fetchQuery<SellerStats>({
        queryKey: [ProductPageQueryKeys.SELLER_STATS],
        queryFn: () => ProductService.getSellerStats(id, serverAxiosRequestData),
      }),
    ];

    const promiseResults = await Promise.allSettled([...mandatoryRequests, ...optionalRequests]);

    const [translations, product, shipping, breadcrumbs, isWelcomeOfferLDFlagEnabled] = promiseResults.map(
      (promiseResult, index) => {
        if (promiseResult.status === 'fulfilled') {
          return promiseResult.value;
        }
        // Throw on error for tracking to pick it up for required requests, the rest can fail
        if (index < mandatoryRequests.length) {
          throw new RequestError(promiseResult.reason as string);
        }
        return null;
      },
    ) as [SSRConfig, Product, Shipping, Breadcrumb[], boolean];

    // Check if the welcome offer banner should be shown on PDP(LD + cookie conditions)
    const isWelcomeOfferClosed = context.req.cookies[AppCookies.PDP_WELCOME_OFFER_CLOSED];
    const showPdpBanner = isWelcomeOfferLDFlagEnabled && !isWelcomeOfferClosed;

    const path = context.req.url;

    // Get the URL for the product that correlates to the current language
    const alternateProductVersion: ContentsLocalizedPath = product?.alternateVersions?.find(
      (version: ContentsLocalizedPath) => {
        return version.language === language;
      },
    );

    // We must redirect to an alternate version if an alternate version exists
    const shouldRedirectToAnAlternateProductPath: boolean =
      !!alternateProductVersion && !path.includes(alternateProductVersion?.path);

    // Redirect to the alternate version of the product if it exists
    // do not redirect if it's a hero pdp request
    if (shouldRedirectToAnAlternateProductPath && !semPdp) {
      const { resolvedUrl } = context;
      const [, queryParams] = resolvedUrl.split('?');
      const hasQueryParams = !!queryParams;

      return {
        redirect: {
          destination: `${alternateProductVersion.path}${hasQueryParams ? `?${queryParams}` : ''}`,
          permanent: true,
        },
      };
    }

    if (product?.alternateVersions) {
      // map the alternate versions to be absolute urls
      product.alternateVersions = product.alternateVersions.map((version: ContentsLocalizedPath) => {
        return {
          ...version,
          path: generateAlternateLink(version),
        };
      });
    }
    // TODO: Refactor this once pdp has been refactored to work with hooks instead of context
    // Endgoal is to use queryClient to do the fetching, but until then, we're filling query client manually with the data
    // fetched above. We do this, so we can move some queries to react-query, while keeping backwards compatibility.
    queryClient.setQueryData([BreadcrumbsQueryKeys.BREADCRUMBS], breadcrumbs);
    queryClient.setQueryData([ProductPageQueryKeys.PRODUCT_SHIPPING], shipping);
    queryClient.setQueryData([ProductPdpPageQueryKeys.GET_PDP_PRODUCT], product);
    queryClient.setQueryData([FeatureFlagsKeys.SEM_LANDING_PAGE_RECOMMENDATION_BLADES], !!semPdp);

    return {
      props: {
        ...initialProps,
        ...translations,
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        product,
        breadcrumbs,
        userAgent: req?.headers?.['user-agent'],
        isWelcomeOfferLDFlagEnabled: !!isWelcomeOfferLDFlagEnabled, // cleanup after A/B tests are done
        showPdpBanner: !!showPdpBanner,
      },
    };
  } catch (e) {
    // Redirect to parent page if a request failed
    if (isRequestError(e)) {
      if (!isRequest404Error(e)) {
        logger.error(
          e,
          'There was an api request error on the Product Page - getServerSideProps - [[...productSlug]].page.tsx',
        );
      }
      const { req } = context;
      // Removes trailing slash
      req.url = req.url.endsWith('/') ? req.url.slice(0, -1) : req.url;

      const pathRedirect: string[] = encodeURI(req.url).split('/');
      pathRedirect.pop();

      return {
        redirect: {
          destination: pathRedirect.join('/') + '/',
          permanent: true,
          basePath: false,
        },
      };
    }
    logger.error(e, 'The server failed to render the Product Page - getServerSideProps - [[...productSlug]].page.tsx');
    // Other errors will get logged
    throw e;
  }
};

export default ProductPage;
